/**
 * Validates uploaded document filenames for Learning Supports / IEP flow.
 * REJECT: documents that are clearly not about student learning support
 * (e.g. sports, newsletters, receipts, team photos).
 * ACCEPT: individual student planning, assessments, surveys, observation notes, etc.
 *
 * Validation is based on filename only (no content scan at upload time).
 */

// Phrases/words in filename that indicate NON–learning-support documents → REJECT
const REJECT_PATTERNS = [
  "athletic",
  "athletics",
  "sports",
  " newsletter",
  "newsletter ",
  "newsletter-",
  "-newsletter",
  "receipt",
  "team photo",
  "basketball",
  "soccer",
  "volleyball",
  "yearbook",
  "school supplies", // often receipt
  "athletic program",
  "sports team",
  "sports season",
  "basketball season",
  "soccer season",
  "football season",
  "volleyball season",
  "team roster",
  "game schedule",
  "tournament",
  "fundraiser",
  "fundraising",
  "invoice"
];

/**
 * Returns true if the file should be REJECTED (not a learning-support document).
 * @param {string} fileName - Original file name (with or without path)
 * @returns {{ rejected: boolean, matchedPattern?: string }}
 */
export function validateLearningSupportDocument(fileName) {
  if (!fileName || typeof fileName !== "string") {
    return { rejected: true, matchedPattern: "empty or invalid name" };
  }
  // Use only the base name (no path) and normalize for matching
  const baseName = fileName.split(/[/\\]/).pop() || "";
  const lower = baseName.toLowerCase().trim();
  if (!lower) return { rejected: true, matchedPattern: "empty filename" };

  for (const pattern of REJECT_PATTERNS) {
    if (lower.includes(pattern.trim().toLowerCase())) {
      return { rejected: true, matchedPattern: pattern };
    }
  }
  return { rejected: false };
}

/**
 * Validate a list of files. Returns first rejection if any, otherwise { rejected: false }.
 * @param {File[]|{ name: string }[]} files
 * @returns {{ rejected: false } | { rejected: true, fileName: string, matchedPattern: string }}
 */
export function validateLearningSupportDocuments(files) {
  if (!files || !Array.isArray(files) || files.length === 0) {
    return { rejected: false };
  }
  for (const file of files) {
    const name = file?.name ?? file?.fileName ?? "";
    const result = validateLearningSupportDocument(name);
    if (result.rejected) {
      return { rejected: true, fileName: name, matchedPattern: result.matchedPattern };
    }
  }
  return { rejected: false };
}
